import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { formatDistanceToNow } from 'date-fns';
import api from 'state/axios';
import toast from 'react-hot-toast';
import {
    useTheme,
    useMediaQuery,
    Badge,
    IconButton,
    Menu,
    MenuItem,
    ListItemAvatar,
    ListItemText,
    Typography,
    Divider,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import BookmarksIcon from '@mui/icons-material/Bookmarks';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import CommentIcon from '@mui/icons-material/Comment';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import AddAlertIcon from '@mui/icons-material/AddAlert';
import { capitalizeFirstLetter } from 'state/helpers';
import { setNotifications, addNotification, removeNotification, clearNotifications } from 'state';
import { getSocket } from 'state/socket';

const iconMap = {
    follow: <BookmarksIcon color="secondary" />,
    bookmark: <BookmarksIcon color="info" />,
    like: <ThumbUpIcon color="success" />,
    comment: <CommentIcon color="inherit" />,
    admin: <ManageAccountsIcon color="primary" />,
    alert: <AddAlertIcon color="warning" />,
};

const getNoticeData = (data = []) => {
    return data.map((item) => {
        const avatar = iconMap[item.type] || <AddAlertIcon color="error" />;
        return {
            id: item._id,
            avatar,
            title: item.type,
            description: item.message,
            datetime: formatDistanceToNow(new Date(item.updatedAt || item.createdAt), { addSuffix: true }),
        };
    });
};

const DropDownNotification = () => {
    const dispatch = useDispatch();
    const notifications = useSelector((state) => state.notifications);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    // Initial fetch of unread notifications — one-time on login
    useEffect(() => {
        api.get('/notifications/unreadnotificationsofuser')
            .then(res => dispatch(setNotifications(res.data.data || [])))
            .catch(() => {});
    }, [dispatch]);

    // Real-time: listen for new notifications via socket
    useEffect(() => {
        const socket = getSocket();
        if (!socket) return;

        const handleNewNotification = (notification) => {
            dispatch(addNotification(notification));
            toast(`${capitalizeFirstLetter(notification.type)}: ${notification.message}`, {
                icon: '🔔',
                duration: 4000,
            });
        };

        socket.on('notification:new', handleNewNotification);
        return () => socket.off('notification:new', handleNewNotification);
    }, [dispatch]);

    const handleRead = useCallback(async (id) => {
        try {
            await api.patch(`/notifications/read/${id}`, {});
            dispatch(removeNotification(id));
        } catch (error) {
            toast.error(error?.response?.data?.message || "Failed to mark notification as read!");
        }
    }, [dispatch]);

    const handleReadAll = useCallback(async () => {
        try {
            await api.patch('/notifications/readallofuser', {});
            dispatch(clearNotifications());
            toast.success('All notifications cleared.');
        } catch (error) {
            toast.error(error?.response?.data?.message || "Failed to mark all notifications as read!");
        }
    }, [dispatch]);

    const handleClick = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const handleClearAll = () => {
        handleReadAll();
        handleClose();
    };

    const handleItemClick = (item) => {
        handleRead(item.id);
        handleClose();
    };

    const noticeList = getNoticeData(notifications);

    return (
        <div>
            <IconButton color="inherit" sx={{ p: 0.5 }} onClick={handleClick}>
                <Badge badgeContent={notifications.length} color="error">
                    <NotificationsIcon />
                </Badge>
            </IconButton>

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
            >
                {noticeList.length === 0 ? (
                    <MenuItem disabled>
                        <ListItemText primary="No notifications found" />
                    </MenuItem>
                ) : (
                    noticeList.map((item) => (
                        <MenuItem
                            key={item.id}
                            sx={{ minWidth: !isMobile ? 225 : 200 }}
                            onClick={() => handleItemClick(item)}
                        >
                            <ListItemAvatar sx={{ pr: 2, minWidth: 2 }}>{item.avatar}</ListItemAvatar>
                            <ListItemText
                                primary={capitalizeFirstLetter(item.title)}
                                secondary={
                                    <>
                                        <Typography component="span" variant="body2" color="text.primary">
                                            {item.description}
                                        </Typography>
                                        <br />
                                        <Typography component="span" variant="caption" color="text.secondary">
                                            {item.datetime}
                                        </Typography>
                                    </>
                                }
                            />
                        </MenuItem>
                    ))
                )}

                {noticeList.length > 0 && (
                    <>
                        <Divider />
                        <MenuItem onClick={handleClearAll} sx={{ justifyContent: 'center' }}>
                            Clear All
                        </MenuItem>
                    </>
                )}
            </Menu>
        </div>
    );
};

export default DropDownNotification;
